import { useEffect } from "react";

interface Meta {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  type?: string;
}

function ensureMeta(attr: "name" | "property", value: string) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  return el;
}

export default function usePageMeta({
  title,
  description,
  keywords,
  canonicalPath = "/",
  type = "website",
}: Meta) {
  useEffect(() => {
    const origin = window.location.origin;
    const basePath = (window as any).__BASE_PATH__ || "";
    const path = canonicalPath.startsWith("/")
      ? `${basePath}${canonicalPath}`
      : `${basePath}/${canonicalPath}`;
    const canonical = `${origin}${path.replace(/\/+/g, "/")}`;

    document.title = title;
    ensureMeta("name", "description").setAttribute("content", description);

    const kw = ensureMeta("name", "keywords");
    if (keywords) kw.setAttribute("content", keywords);
    else kw.removeAttribute("content");

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    const ogTitle = ensureMeta("property", "og:title");
    ogTitle.setAttribute("content", title);
    const ogDesc = ensureMeta("property", "og:description");
    ogDesc.setAttribute("content", description);
    const ogUrl = ensureMeta("property", "og:url");
    ogUrl.setAttribute("content", canonical);
    const ogType = ensureMeta("property", "og:type");
    ogType.setAttribute("content", type);
    const twCard = ensureMeta("name", "twitter:card");
    twCard.setAttribute("content", "summary_large_image");
    const twTitle = ensureMeta("name", "twitter:title");
    twTitle.setAttribute("content", title);
    const twDesc = ensureMeta("name", "twitter:description");
    twDesc.setAttribute("content", description);
  }, [title, description, keywords, canonicalPath, type]);
}

/** 注入 JSON-LD 结构化数据 */
export function useJsonLd(blocks: Record<string, unknown>[]) {
  const key = JSON.stringify(blocks);
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = key;
    script.setAttribute("data-page-jsonld", "true");
    document.head.appendChild(script);
    return () => {
      document.head.querySelectorAll("script[data-page-jsonld]").forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}